/**
 * @prettier
 */
import {createSelector} from 'reselect';

import {
    getAbacusTests,
    getCarousel,
    getContactOwnerForm,
    getSystem,
    getTranslations,
    getMap,
    getHeader,
    getTravelerFeatures,
    getTripData
} from './store';
import getPriceDetailsData from './priceDetails';
import getInquiryState from './inquiry';
import getRateDetailsData from './rateDetails';
import {labelMaker} from '../../server/utils/labelMaker';
import getIndexSibling from '../../server/utils/carouselUtils';
import {getListingPageModalStates, isInquiryModalOpen} from './modalStates';
import getAvailabilityCalendar from './availabilityCalendar';
import getHouseRulesData from './houseRules';
import getRecommendedProperties from './recommendedProperties';
import getBookingUrl from './bookingUrl';
import useCancellationPolicy from './cancellationPolicy';
import getBookingModalData from './bookingModal';
import {getListingLabels} from './listingLabels';
import {getJsReviews, getBearStayBannerLabels} from './reviews';
import {getListingSummary} from './listingSummary';
import {getGeoRelativeDistance} from './listing';
import {
    getArrivedFromExternalSite,
    getBaseUrlPath,
    getBrandName,
    getLocale,
    getSystemClientSideRendered,
    getIsMobile
} from './system';
import {
    getAccomodationSummary,
    getBearStayReviewData,
    getBookingType,
    getDescription,
    getHostType,
    getListingCarousel,
    getGeoCode,
    getPropertyNotes,
    getPropertyName,
    getSearchLandingPages,
    getListingIsFamilyFriendly,
    getVirtualTourUrl,
    getIsMatterportVirtualTour
} from './listing';
import removeUniId from '../utils/recommendedLinksUtils';
import {isInBucketData} from '../utils/abacusHelper';
import {splitIntoTwoColumns} from '../utils/photoGallery';

const getMapData = createSelector(
    [getLocale, getMap, getGeoCode, getSystem, getTranslations],
    (locale, map, geoCode, system, translations) => {
        const mapRegion = locale.substring(3, 5);
        return {
            unveiled: map.unveiled,
            exact: geoCode && !!geoCode.exact,
            lat: geoCode && geoCode.latitude ? geoCode.latitude : 0,
            lng: geoCode && geoCode.longitude ? geoCode.longitude : 0,
            mapApiKey: map.apiKey,
            mapLanguage: locale,
            mapRegion,
            labels: {
                openMap: translations.openMap
            },
            siteName: system.siteName
        };
    }
);

const getListingDescription = createSelector(
    [getAccomodationSummary, getDescription, getPropertyName],
    (accomodationSummary, description, propertyName) => {
        return {
            propertyName: `${propertyName}`,
            summary: `${accomodationSummary}`,
            description: `${description}`
        };
    }
);

/* eslint-disable complexity */
const getImages = createSelector(
    [
        getListingCarousel,
        getVirtualTourUrl,
        getIsMatterportVirtualTour,
        getAbacusTests,
        getIsMobile
    ],
    (listingCarousel, virtualTourUrl, isMatterportVirtualTour, abacusTests, isMobile) => {
        let {images} = listingCarousel;

        let shouldVRImageIncluded = false;
        // Determine if VRTour should be included.
        if (virtualTourUrl) {
            if (isMobile) {
                if (isInBucketData(abacusTests, 'ha_virtual_tour_viewer_pdp_mobile', 1)) {
                    shouldVRImageIncluded = true;
                }
            } else if (isMatterportVirtualTour) {
                if (isInBucketData(abacusTests, 'ha_pdp_virtual_tour_matterport', 1)) {
                    shouldVRImageIncluded = true;
                }
            } else if (isInBucketData(abacusTests, 'ha_pdp_virtual_tour_viewer', 1)) {
                shouldVRImageIncluded = true;
            }
        }

        if (shouldVRImageIncluded) {
            const virtualTourObject = {
                mediaType: 'virtualtour',
                lowResSrc:
                    'https://odis.homeaway.com/odis/listing/202cb713-2fd0-4959-8003-9a70b6941c77.c10.jpg',
                src:
                    'https://odis.homeaway.com/odis/listing/202cb713-2fd0-4959-8003-9a70b6941c77.c10.jpg',
                altText: 'Take a virtual tour',
                width: 1024,
                height: 768
            };

            images = images
                .slice(0, 2)
                .concat(virtualTourObject)
                .concat(images.slice(2));
        }
        return images;
    }
);
/* eslint-enable complexity */

const getPhotoGalleryImages = createSelector(
    [getImages],
    (images) => {
        const photoGalleryImages = images
            .map((image, index) => {
                return {...image, carouselIndex: index};
            })
            .filter(({mediaType}) => mediaType === 'image');

        return splitIntoTwoColumns(photoGalleryImages);
    }
);

const getVirtualTourCoverImage = createSelector(
    [getImages],
    (images) => {
        const firstLandscapeImage = images.find((element) => {
            return element.width > element.height;
        });

        return firstLandscapeImage || images[0];
    }
);

const getCarouselData = createSelector(
    [
        getSystemClientSideRendered,
        getCarousel,
        getListingCarousel,
        getTranslations,
        getAbacusTests,
        getVirtualTourUrl,
        getImages
    ],
    (
        clientSideRendered,
        carousel,
        listingCarousel,
        translations,
        abacusTests,
        virtualTourUrl,
        images
    ) => {
        const useVerticalGallery =
            isInBucketData(abacusTests, 'HA_PDP_VerticalGalleryMobile', 1) ||
            isInBucketData(abacusTests, 'ha_pdp_desktop_gallery', 2);

        const prevIndex = getIndexSibling(false, carousel.index, images);
        const nextIndex = getIndexSibling(true, carousel.index, images);
        const prevPrevIndex = getIndexSibling(false, prevIndex, images);
        const nextNextIndex = getIndexSibling(true, nextIndex, images);
        const renderedIndices = [
            prevPrevIndex,
            prevIndex,
            carousel.index,
            nextIndex,
            nextNextIndex
        ];

        return {
            galleryTitle: labelMaker(translations.photoGallery, images.length),
            fullScreen: carousel.fullScreen,
            isVerticalGalleryOpen: carousel.isVerticalGalleryOpen,
            renderInterative: clientSideRendered,
            thumbnailUrl: listingCarousel.thumbnailUrl,
            images,
            index: carousel.index,
            prevIndex,
            renderedIndices,
            hasImages: !!images.length,
            useVerticalGallery,
            virtualTourUrl
        };
    }
);

const getInquiryModalTitle = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        return hostType.owner ? trans.contactOwner : trans.contactManager;
    }
);

const getInquiryModalScrollTop = createSelector(
    [getContactOwnerForm, isInquiryModalOpen],
    (contactOwnerForm, inquiryModalOpen) => {
        const shouldScroll = contactOwnerForm.scrollTop;
        return inquiryModalOpen && shouldScroll;
    }
);

const getReviewsScrollSelector = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.reviewId ? `[data-review="${reviewData.reviewId}"]` : '';
    }
);

const getReviewsModalScrollTop = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.scrollTop;
    }
);

const getOwnerSectionTitle = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        return hostType.owner ? trans.owner : trans.propertyManager;
    }
);

const getLocationDescription = createSelector(
    [getPropertyNotes],
    (propertyNotes = []) => {
        for (let note of propertyNotes) {
            if (note.title === 'Local Description' && note.text && note.text.trim().length) {
                return note.text;
            }
        }

        return null;
    }
);

const getSlpBreadcrumbLinks = createSelector(
    [getSearchLandingPages],
    (searchLandingPages) => {
        if (!searchLandingPages) {
            return {};
        }
        const {breadcrumbs = []} = searchLandingPages;
        const targetNum = 8;
        const linkArray = [];

        for (let crumb of breadcrumbs) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (crumb.path && crumb.url) {
                linkArray.push({text: crumb.name, url: crumb.url});
            }
        }

        return {links: linkArray, isBreadcrumbs: true};
    }
);

/* eslint-disable complexity */
const getSlpRelatedLinks = createSelector(
    [getSearchLandingPages, getSlpBreadcrumbLinks],
    (searchLandingPages, slpBreadcrumbLinks) => {
        if (!searchLandingPages) {
            return {};
        }

        const {descendantsByType = {}, adjacentsByType = {}} = searchLandingPages;
        const allLinks = [
            ...(descendantsByType.pointOfInterest || []),
            ...(descendantsByType.neighborhood || []),
            ...(adjacentsByType.pointOfInterest || []),
            ...(adjacentsByType.neighborhood || [])
        ];
        const targetNum = 8 - slpBreadcrumbLinks.links.length;
        const linkArray = [];

        for (let link of allLinks) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (link.path && link.url) {
                linkArray.push({text: link.simpleName, url: link.url});
            }
        }

        return {links: linkArray};
    }
);
/* eslint-enable complexity */

const getRecommendedPropertiesLinks = createSelector(
    [getRecommendedProperties],
    (recommendedProperties) => {
        const linkArray = [];
        if (
            !(recommendedProperties.recommendedHits && recommendedProperties.recommendedHits.length)
        ) {
            return {};
        }

        for (let property of recommendedProperties.recommendedHits) {
            linkArray.push({
                text: property.headline,
                url: removeUniId(property.propertyHref)
            });
        }
        return {links: linkArray};
    }
);

const getSeoLinks = createSelector(
    [getSlpBreadcrumbLinks, getSlpRelatedLinks, getRecommendedPropertiesLinks],
    (slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks) => {
        const allLinksArray = [];

        for (let linkGroup of [slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks]) {
            if ((linkGroup.links || []).length) {
                allLinksArray.push(linkGroup);
            }
        }

        return allLinksArray;
    }
);

const getDisplayImprint = createSelector(
    [getTravelerFeatures],
    (travelerFeatures) => {
        return !!travelerFeatures.displayImprint;
    }
);

const getIsFamilySegment = createSelector(
    [getTripData],
    (tripData) => {
        return !!(tripData && tripData.isFamilySegment);
    }
);

const getListingPageData = createSelector(
    [
        getAbacusTests,
        getArrivedFromExternalSite,
        getAvailabilityCalendar,
        getBearStayBannerLabels,
        getBearStayReviewData,
        getBookingModalData,
        getBookingType,
        getBookingUrl,
        getBrandName,
        getCarouselData,
        getGeoRelativeDistance,
        getHeader,
        getHouseRulesData,
        getInquiryModalScrollTop,
        getInquiryModalTitle,
        getInquiryState,
        getIsFamilySegment,
        getListingDescription,
        getListingLabels,
        getBaseUrlPath,
        getListingPageModalStates,
        getListingSummary,
        getLocationDescription,
        getMapData,
        getOwnerSectionTitle,
        getPhotoGalleryImages,
        getPriceDetailsData,
        getRateDetailsData,
        getRecommendedProperties,
        getReviewsModalScrollTop,
        getReviewsScrollSelector,
        getSeoLinks,
        getSystem,
        getTranslations,
        getTravelerFeatures,
        getDisplayImprint,
        getListingIsFamilyFriendly,
        useCancellationPolicy,
        getVirtualTourCoverImage,
        getVirtualTourUrl,
        getIsMatterportVirtualTour
    ],
    (
        abacusTestData,
        arrivedFromExternalSite,
        availabilityCalendar,
        bearStayBannerLabels,
        bearStayReviewData,
        bookingModalData,
        bookingType,
        bookingUrl,
        brandName,
        carouselData,
        geoRelativeDistance,
        header,
        houseRulesData,
        inquiryModalScrollTop,
        inquiryModalTitle,
        inquiryState,
        isFamilySegment,
        listingDescription,
        listingLabels,
        baseUrlPath,
        listingPageModalStates,
        listingSummary,
        locationDescription,
        mapData,
        ownerSectionTitle,
        photoGalleryImages,
        priceDetailsData,
        rateDetailsData,
        recommendedProperties,
        reviewsModalScrollTop,
        reviewsScrollSelector,
        seoLinks,
        system,
        translations,
        travelerFeatures,
        displayImprint,
        isFamilyFriendly,
        cancellationPolicy,
        virtualTourCoverImage,
        virtualTourUrl,
        isMatterportVirtualTour
    ) => {
        return {
            abacusTestData,
            arrivedFromExternalSite,
            availabilityCalendar,
            bearStayBannerLabels,
            bearStayReviewData,
            bookingModalData,
            bookingUrl,
            brandName,
            cancellationPolicy,
            carouselData,
            displayBookingAssistance: travelerFeatures.displayBookingAssistance,
            displayCivilCodeDisclaimerLink: travelerFeatures.displayCivilCodeDisclaimerLink,
            geoRelativeDistance,
            header,
            houseRulesData,
            inquiryModalScrollTop,
            inquiryModalTitle,
            inquiryState,
            isFamilySegment,
            isTravelerLoggedIn: travelerFeatures.isLoggedIn,
            labels: translations,
            listingDescription,
            listingLabels,
            baseUrlPath,
            listingPageModalStates,
            listingSummary,
            locationDescription,
            mapData,
            ownerSectionTitle,
            photoGalleryImages,
            priceDetailsData,
            propertyOlb: bookingType.onlineBookable,
            rateDetailsData,
            recommendedProperties,
            reviewsModalScrollTop,
            reviewsScrollSelector,
            showSeoLinks: system.siteName !== 'vrbo',
            seoLinks,
            system,
            displayImprint,
            isFamilyFriendly,
            virtualTourCoverImage,
            virtualTourUrl,
            isMatterportVirtualTour
        };
    }
);

export default getListingPageData;
export {
    getCarouselData,
    getInquiryModalScrollTop,
    getInquiryModalTitle,
    getListingDescription,
    getListingPageData,
    getLocationDescription,
    getOwnerSectionTitle,
    getReviewsModalScrollTop,
    getMapData,
    getSlpBreadcrumbLinks,
    getSlpRelatedLinks,
    getRecommendedPropertiesLinks,
    getSeoLinks
};
